<script lang="ts">
  // Signing in with a work account. Reached only by typing the address — there is no
  // link to it from the buyer's app, because it is not for the buyer.

  import { keyFor } from '$lib/crypto/device-keys';
  import { t } from '$lib/i18n/index.svelte';
  import { asClientError } from '$lib/protocol/errors';
  import { operatorSignIn, STAFF_KEY_ALIAS } from '$lib/protocol/operator-api';
  import {
    loadStaffState,
    saveStaffSignIn,
    staffLoaded,
    staffServerUrl,
  } from '$lib/state/staff.svelte';

  import { navigate } from '../../../app/router.svelte';

  let serverUrl = $state('');
  let login = $state('');
  let password = $state('');
  let busy = $state(false);
  let problem = $state('');

  $effect(() => {
    if (!staffLoaded()) {
      void loadStaffState().then(() => {
        serverUrl = staffServerUrl();
      });
    } else if (serverUrl.length === 0) {
      serverUrl = staffServerUrl();
    }
  });

  async function submit(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    busy = true;
    problem = '';
    try {
      const key = await keyFor(STAFF_KEY_ALIAS);
      const session = await operatorSignIn({ serverUrl, login, password, key });
      await saveStaffSignIn(serverUrl, session);
      password = '';
      navigate({ name: 'staff' });
    } catch (error) {
      // The server does not say whether the login or the password was wrong, and
      // neither do we.
      problem = asClientError(error).message;
    } finally {
      busy = false;
    }
  }
</script>

<header class="bar"><h1>{t('staff.title')}</h1></header>

<form onsubmit={submit}>
  <label>
    {t('staff.server')}
    <input type="url" bind:value={serverUrl} required placeholder="https://…" />
  </label>
  <label>
    {t('staff.login')}
    <input type="text" bind:value={login} required autocomplete="username" />
  </label>
  <label>
    {t('staff.password')}
    <input type="password" bind:value={password} required autocomplete="current-password" />
  </label>
  {#if problem.length > 0}
    <p class="problem" role="alert">{problem}</p>
  {/if}
  <button type="submit" disabled={busy}>{t('staff.signIn')}</button>
</form>

<style>
  .bar {
    padding: 10px 16px;
  }
  h1 {
    margin: 0;
    font-size: 20px;
  }
  form {
    padding: 0 16px 24px;
    display: flex;
    flex-direction: column;
    gap: 14px;
    overflow-y: auto;
  }
  label {
    display: flex;
    flex-direction: column;
    gap: 6px;
    font-size: 13px;
    color: var(--ink-soft);
  }
  input {
    border: 1px solid var(--line);
    border-radius: 8px;
    background: transparent;
    color: var(--ink);
    font: inherit;
    padding: 10px;
  }
  button {
    border: 1px solid var(--line);
    border-radius: 10px;
    background: var(--surface);
    color: var(--ink);
    font: inherit;
    font-weight: 600;
    padding: 11px;
    cursor: pointer;
  }
  .problem {
    margin: 0;
    color: var(--danger, #b3261e);
    font-size: 13px;
  }
</style>
